import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../api/client';
import { AppError, errorMessage } from '../../api/errors';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, Button, Card } from '../../design';
import { useToast } from '../../design/Toast';
import { fmtDateTime, fmtRelative } from '../../lib/format';
import type { LockerDetail } from './common';
import s from './lockers.module.css';

/** Codul de înrolare (afișat o singură dată), dispozitivul înrolat, revocare, ping și reboot. */
export function DevicePanel({ locker: l }: { locker: LockerDetail }) {
  const { has, withStepUp } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const [pingResult, setPingResult] = useState<string | null>(null);
  const device = useQuery({
    queryKey: ['locker', l.id, 'device'],
    queryFn: () => unwrap(api.GET('/lockers/{id}/device', { params: { path: { id: l.id } } })),
    retry: false,
  });
  const noDevice = device.error instanceof AppError && device.error.status === 404;

  const regenerate = useMutation({
    mutationFn: () =>
      unwrap(api.POST('/lockers/{id}/enrollment-code', { params: { path: { id: l.id } } })),
    onSuccess: setCode,
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const revoke = useMutation({
    mutationFn: () =>
      withStepUp((headers) =>
        unwrap(
          api.POST('/lockers/{id}/device/revoke', {
            params: { path: { id: l.id }, header: headers },
          }),
        ),
      ),
    onSuccess: () => {
      toast(
        'Dispozitivul a fost revocat; lockerul trece offline până la o nouă înrolare.',
        'success',
      );
      void qc.invalidateQueries({ queryKey: ['locker', l.id] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const ping = useMutation({
    mutationFn: () =>
      unwrap(api.POST('/lockers/{id}/commands/ping', { params: { path: { id: l.id } } })),
    onSuccess: (r) =>
      setPingResult(r.ok ? `ack în ${r.latencyMs} ms` : `eșuat: ${r.error ?? 'fără ack'}`),
    onError: (e) => setPingResult(errorMessage(e)),
  });
  const reboot = useMutation({
    mutationFn: () =>
      withStepUp((headers) =>
        unwrap(
          api.POST('/lockers/{id}/commands/reboot', {
            params: { path: { id: l.id }, header: headers },
          }),
        ),
      ),
    onSuccess: (r) =>
      toast(
        r.ok ? 'Kioskul repornește.' : `Repornirea a eșuat: ${r.error ?? 'fără ack'}`,
        r.ok ? 'success' : 'error',
      ),
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  return (
    <Card>
      <h2>Dispozitiv (kiosk)</h2>
      {device.isLoading && <p className="muted small">Se încarcă…</p>}
      {noDevice && (
        <div className="stack" style={{ marginTop: 12 }}>
          <p className="muted small">
            Niciun dispozitiv înrolat. Generează un cod, tastează-l pe kiosk în ecranul de înrolare;
            codul este valabil 24 de ore și se afișează o singură dată.
          </p>
          {code ? (
            <div className="flex wrap">
              <span className={s.code} aria-label="Cod de înrolare">
                {code.code}
              </span>
              <Button
                small
                icon="copy"
                onClick={() =>
                  void navigator.clipboard
                    ?.writeText(code.code)
                    .then(() => toast('Cod copiat.', 'success'))
                }
              >
                Copiază
              </Button>
              <span className="small muted">expiră {fmtDateTime(code.expiresAt)}</span>
            </div>
          ) : (
            has('hardware') && (
              <div>
                <Button
                  variant="primary"
                  icon="key"
                  onClick={() => regenerate.mutate()}
                  busy={regenerate.isPending}
                >
                  Generează cod de înrolare
                </Button>
              </div>
            )
          )}
        </div>
      )}
      {device.data && (
        <div className="stack" style={{ marginTop: 12 }}>
          <dl
            className="small"
            style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 14px', margin: 0 }}
          >
            <dt className="muted">Hardware</dt>
            <dd className="mono">{device.data.hardwareId}</dd>
            <dt className="muted">Aplicație</dt>
            <dd>{device.data.appVersion || '—'}</dd>
            <dt className="muted">Înrolat</dt>
            <dd>{fmtDateTime(device.data.enrolledAt)}</dd>
            <dt className="muted">Ultima conectare</dt>
            <dd>{fmtRelative(device.data.lastSeenAt)}</dd>
            <dt className="muted">Stare</dt>
            <dd>
              {device.data.revokedAt ? (
                <Badge tone="red">revocat</Badge>
              ) : (
                <Badge tone="green">activ</Badge>
              )}
            </dd>
          </dl>
          {has('hardware') && (
            <div className="flex wrap">
              <Button
                small
                icon="refresh"
                onClick={() => ping.mutate()}
                busy={ping.isPending}
                disabled={l.status === 'offline' || l.status === 'unprovisioned'}
              >
                Ping
              </Button>
              {pingResult && <span className="small muted">{pingResult}</span>}
              <Button
                small
                onClick={() => reboot.mutate()}
                busy={reboot.isPending}
                disabled={l.status === 'offline' || l.status === 'unprovisioned'}
              >
                Reboot (step-up)
              </Button>
              <Button
                small
                variant="danger"
                onClick={() => revoke.mutate()}
                busy={revoke.isPending}
              >
                Revocă dispozitivul
              </Button>
            </div>
          )}
          {has('hardware') && code && (
            <div className="flex wrap">
              <span className={s.code}>{code.code}</span>
              <span className="small muted">
                cod nou de înrolare; înlocuiește dispozitivul curent la prima folosire
              </span>
            </div>
          )}
          {has('hardware') && !code && (l.status === 'offline' || l.status === 'unprovisioned') && (
            <Button
              small
              icon="key"
              onClick={() => regenerate.mutate()}
              busy={regenerate.isPending}
            >
              Regenerează codul de înrolare
            </Button>
          )}
        </div>
      )}
      {device.error && !noDevice && (
        <p style={{ color: 'var(--red)' }}>{errorMessage(device.error)}</p>
      )}
    </Card>
  );
}
